import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Button, Card, Descriptions, Form, Input, Modal, Select, Space, Table, Tag, message } from "antd";
import { PageHeader } from "../../components/PageHeader";
import { SiteLabel } from "../../components/SiteLabel";
import { useApi } from "../../helpers/useApi";
import { fmtItem } from "../../helpers/format";
import { hasAnyGrant } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { useSitesQuery } from "../../helpers/queries";
import type { ToolCustody } from "../../api-models";

const STATUS_COLORS: Record<string, string> = {
  ISSUED: "blue",
  TRANSFERRED: "geekblue",
  RETURNED: "green",
  LOST: "red",
  DAMAGED: "orange",
};

export default function ToolDetailPage() {
  const { id } = useParams();
  const { user } = useUser();
  const { request, loading } = useApi();
  const { data: sites = [] } = useSitesQuery();
  const [tool, setTool] = useState<ToolCustody | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [transferForm] = Form.useForm();
  const [reportForm] = Form.useForm();
  const canWrite = hasAnyGrant(user, "Tools.Write");

  async function load() {
    setTool(await request<ToolCustody>({ url: `/tools/${id}` }));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function transfer() {
    const values = await transferForm.validateFields();
    await request({ method: "POST", url: `/tools/${id}/transfer`, data: values });
    message.success("Custody transferred.");
    setTransferOpen(false);
    load();
  }

  async function returnTool() {
    await request({ method: "POST", url: `/tools/${id}/return` });
    message.success("Tool returned.");
    load();
  }

  async function report() {
    const values = await reportForm.validateFields();
    await request({ method: "POST", url: `/tools/${id}/report`, data: values });
    message.success("Condition recorded.");
    setReportOpen(false);
    load();
  }

  if (!tool) return null;
  const active = tool.status === "ISSUED" || tool.status === "TRANSFERRED";

  return (
    <div>
      <PageHeader
        title={tool.item ? fmtItem(tool.item) : `Tool #${tool.id}`}
        subtitle={tool.serializedAssetId || undefined}
        extra={
          <Space wrap>
            <Tag color={STATUS_COLORS[tool.status]}>{tool.status}</Tag>
            {active && canWrite && (
              <Button onClick={() => setTransferOpen(true)} data-tour="tool-transfer">
                Transfer
              </Button>
            )}
            {active && canWrite && <Button onClick={returnTool}>Return to Central</Button>}
            {active && canWrite && (
              <Button danger onClick={() => setReportOpen(true)}>
                Report damage/loss
              </Button>
            )}
          </Space>
        }
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Custody, not consumption"
        description="This register tracks who holds the tool. Assigning it to a site is never consumption — any physical stock movement happens through the ordinary MRQ → GIN → MRN or Transfer Order flow, and damage or loss on a stock-controlled tool still needs its own stock adjustment."
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Item">{fmtItem(tool.item)}</Descriptions.Item>
          <Descriptions.Item label="Held at"><SiteLabel site={tool.site} fallback="Central" /></Descriptions.Item>
          <Descriptions.Item label="Quantity">{tool.qty}</Descriptions.Item>
          <Descriptions.Item label="Asset ID">{tool.serializedAssetId || "—"}</Descriptions.Item>
          <Descriptions.Item label="Issued">{new Date(tool.issueDate).toLocaleDateString()}</Descriptions.Item>
          <Descriptions.Item label="Condition">{tool.condition || "—"}</Descriptions.Item>
          <Descriptions.Item label="Notes" span={2}>
            {tool.notes || "—"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="Custody history">
        <Table
          rowKey="id"
          dataSource={tool.history ?? []}
          pagination={false}
          columns={[
            { title: "When", render: (_, r) => new Date(r.createdAt).toLocaleString() },
            { title: "Event", dataIndex: "eventType", render: (v: string) => <Tag>{v.replace(/_/g, " ")}</Tag> },
            { title: "From", render: (_, r) => (r.fromSiteId ? sites.find((s) => s.id === r.fromSiteId)?.name : "Central") },
            { title: "To", render: (_, r) => (r.toSiteId ? sites.find((s) => s.id === r.toSiteId)?.name : "Central") },
            { title: "Notes", dataIndex: "notes", render: (v) => v || "—" },
          ]}
        />
      </Card>

      <Modal title="Transfer custody" open={transferOpen} onCancel={() => setTransferOpen(false)} onOk={transfer} confirmLoading={loading} destroyOnHidden>
        <Form form={transferForm} layout="vertical">
          <Form.Item name="toSiteId" label="To site" extra="Leave empty to move the tool back to Central.">
            <Select allowClear options={sites.map((s) => ({ value: s.id, label: s.name }))} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Report damage or loss" open={reportOpen} onCancel={() => setReportOpen(false)} onOk={report} confirmLoading={loading} destroyOnHidden>
        <Form form={reportForm} layout="vertical" initialValues={{ status: "DAMAGED" }}>
          <Form.Item name="status" label="Condition" rules={[{ required: true }]}>
            <Select
              options={[
                { value: "DAMAGED", label: "Damaged" },
                { value: "LOST", label: "Lost" },
              ]}
            />
          </Form.Item>
          <Form.Item name="notes" label="What happened?" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
